import {
    Box,
    Link,
    Stack,
    Typography,
    useTheme,
    darken,
    lighten
} from "@mui/material";
import {EventResponse} from "../../data/events/EventResponse";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import useApiCall from "../../hooks/CancellableApiCall";
import {EventPageinatedResponse} from "../../data/events/EventPageinatedResponse";

function formatEventDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const timeStr = date.toLocaleTimeString(window.navigator.language, {
        hour: "2-digit",
        minute: "2-digit"
    });

    if (date.toDateString() === now.toDateString()) return `today at ${timeStr}`;
    if (date.toDateString() === tomorrow.toDateString()) return `tomorrow at ${timeStr}`;

    return date.toLocaleDateString(window.navigator.language, {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit"
    });
}

interface EventBannerProps {
    event: EventResponse;
}

const EventBanner = (props: EventBannerProps) => {
    const {event} = props;
    const theme = useTheme();
    const navigate = useNavigate();

    const bgColor = theme.palette.mode === "dark"
        ? lighten(theme.palette.background.default, 0.08)
        : darken(theme.palette.background.default, 0.06);

    const hoverColor = theme.palette.mode === "dark"
        ? lighten(theme.palette.background.default, 0.12)
        : darken(theme.palette.background.default, 0.1);

    return (
        <Box
            sx={{
                px: 2,
                py: 1.25,
                backgroundColor: bgColor,
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": {backgroundColor: hoverColor}
            }}
            onClick={() => navigate(`/event/view/${event.id}`)}
        >
            <Typography variant="body2">
                {event.type.displayName} {formatEventDate(event.date)}
                {event.host && (
                    <Typography component="span" variant="body2" color="text.secondary">
                        {" "}— hosted by{" "}
                        <Link
                            underline="none"
                            component={RouterLink}
                            to={`/user/${event.host.communityId}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {event.host.nick}
                        </Link>
                    </Typography>
                )}
            </Typography>
        </Box>
    );
};

export const UpcomingEventBanners = () => {
    const eventData = useApiCall<EventPageinatedResponse>({
        url: "/api/event/1"
    });

    const events = eventData.data?.events?.slice(0, 3) ?? [];

    if (events.length === 0) {
        return null;
    }

    return (
        <Stack spacing={0.5} sx={{mb: 1}}>
            {events.map(event => (
                <EventBanner key={event.id} event={event}/>
            ))}
        </Stack>
    );
};
